import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../api/client';
import styles from './InventoryPage.module.css';

const getInventory = () => api.get('/admin/inventory').then(r => r.data.data);
const updateInventory = ({ productId, stockCount }: { productId: string; stockCount: number }) =>
  api.put(`/admin/inventory/${productId}`, { stockCount }).then(r => r.data.data);

export default function InventoryPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Record<string, number>>({});

  const { data, isLoading } = useQuery({ queryKey: ['admin-inventory'], queryFn: getInventory });

  const mutation = useMutation({
    mutationFn: updateInventory,
    onSuccess: (_, { productId }) => {
      qc.invalidateQueries({ queryKey: ['admin-inventory'] });
      setEditing(e => { const n = {...e}; delete n[productId]; return n; });
      toast.success('Stock updated');
    },
    onError: () => toast.error('Update failed'),
  });

  if (isLoading) return <div className={styles.loading}>{t('common.loading')}</div>;

  const inventory = data?.inventory || [];
  const lowStock = inventory.filter((i: Record<string, unknown>) => (i.stockCount as number) <= (i.lowStockThreshold as number));

  return (
    <div>
      <h1 className={styles.pageTitle}>{t('admin.inventory')}</h1>

      {lowStock.length > 0 && (
        <div className={styles.alert}>
          <AlertTriangle size={18} />
          <span><strong>{lowStock.length}</strong> {lowStock.length === 1 ? 'product is' : 'products are'} low on stock</span>
        </div>
      )}

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr><th>Product</th><th>Current Stock</th><th>Low Stock Alert</th><th>Last Updated</th><th>Action</th></tr>
          </thead>
          <tbody>
            {inventory.map((item: Record<string, unknown>) => {
              const pid = item.productId as string;
              const stock = item.stockCount as number;
              const threshold = item.lowStockThreshold as number;
              const isLow = stock <= threshold;
              return (
                <tr key={pid} className={isLow ? styles.lowRow : ''}>
                  <td>
                    <div className={styles.productName}>{item.productName as string || pid.slice(0, 12)}</div>
                    <div className={styles.productId}>{pid.slice(0, 8)}…</div>
                  </td>
                  <td>
                    <div className={styles.stockCell}>
                      {isLow && <AlertTriangle size={14} color="#e65100" />}
                      <input
                        type="number"
                        min="0"
                        defaultValue={stock}
                        className={styles.stockInput}
                        onChange={e => setEditing(prev => ({ ...prev, [pid]: parseInt(e.target.value) || 0 }))}
                      />
                    </div>
                  </td>
                  <td><span className={isLow ? styles.lowBadge : styles.okBadge}>{isLow ? `Low (≤${threshold})` : 'OK'}</span></td>
                  <td className={styles.date}>{new Date(item.updatedAt as string).toLocaleDateString('en-IN')}</td>
                  <td>
                    {pid in editing && (
                      <button
                        className={styles.saveBtn}
                        onClick={() => mutation.mutate({ productId: pid, stockCount: editing[pid] })}
                        disabled={mutation.isPending}
                      >
                        Save
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
